import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { FadeIn, MaskedLines, Overline, Chapter } from "@/components/Motion";
import { IMAGES } from "@/data/content";

const EUROPEAN_PILLARS = [
  "European product vision",
  "Premium industrial design",
  "Distributor-first philosophy",
  "Continuous innovation",
  "Long-term technological development",
];

const RD_ACTIVITIES = [
  "Mechanical Engineering",
  "Industrial Design",
  "Electronics",
  "Embedded Software",
  "Brushless Drive Systems",
  "Outdoor Technologies",
  "Future Modular Rail Systems",
  "AI Measurement Technologies",
];

const PLATFORM_PILLARS = [
  "European Strategy",
  "Global Engineering",
  "Chinese Manufacturing Excellence",
  "European Rail Production",
  "OEM Manufacturing",
  "ODM Development",
  "AI Dealer Services",
  "Continuous Product Innovation",
];

const TIMELINE = [
  { year: "1999", title: "Manufacturing expertise begins", text: "Our production facility starts building stairlifts in Pujiang County." },
  { year: "25+", title: "Years of continuous production", text: "More than two decades of stairlift manufacturing, refinement and process improvement." },
  { year: "25,000+", title: "Stairlifts worldwide", text: "Manufactured and installed across homes and markets around the globe." },
  { year: "Today", title: "ACTIVE launches", text: "A European industrial platform built on this proven manufacturing foundation." },
  { year: "Next", title: "MODULAR RAIL™ & AIMEASURE™", text: "Proprietary rail systems and AI measurement technologies enter the platform." },
];

export default function Company() {
  return (
    <>
      <PageHero
        overline="Company"
        lines={["Engineering", "Without Borders"]}
        sub="ACTIVE Stairlifts is a European stairlift company built on a unique combination of European vision, advanced Chinese engineering and global manufacturing excellence."
      />

      <section className="bg-white pb-24" data-testid="company-mission">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="rounded-2xl bg-obsidian noise-dark border border-white/10 p-9 sm:p-14">
              <p className="relative z-[2] font-mono text-[10px] uppercase tracking-[0.25em] text-signal">Our Mission</p>
              <p className="relative z-[2] mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white leading-[1.15] max-w-3xl">
                <MaskedLines lines={["Develop the next generation of stairlift", "technologies for distributors worldwide."]} />
              </p>
              <p className="relative z-[2] mt-6 text-base text-slate-400 leading-relaxed max-w-2xl">
                Rather than choosing between European engineering and Asian manufacturing, ACTIVE combines the strengths of both worlds into a single industrial platform.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-[#F8F9FA] border-y border-slate-200/70 py-24" data-testid="company-european">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <Chapter index="01" label="A European Company" />
            <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-slate-950 leading-[1.05]">
              <MaskedLines lines={["European vision,", "distributor-first"]} />
            </h2>
            <FadeIn delay={0.15}>
              <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-lg">
                ACTIVE is a European brand focused on innovation, product development and long-term partnerships with distributors across Europe.
              </p>
              <p className="mt-4 text-base text-slate-950 font-medium leading-relaxed max-w-lg">
                Our ambition is to become Europe's most innovative stairlift manufacturing platform.
              </p>
            </FadeIn>
          </div>
          <div>
            {EUROPEAN_PILLARS.map((p, i) => (
              <FadeIn key={p} delay={Math.min(i * 0.05, 0.25)}>
                <div className="group flex items-center gap-6 py-5 border-b border-slate-200/80 first:border-t" data-testid={`pillar-eu-${i}`}>
                  <span className="font-mono text-[11px] text-signal tracking-[0.15em] w-7 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-lg font-semibold tracking-tight text-slate-950 group-hover:text-signal transition-colors">{p}</h3>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-obsidian noise-dark py-24 sm:py-32" data-testid="company-rd">
        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="02" label="Research & Development" light />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            <div>
              <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-white leading-[1.05]">
                <MaskedLines lines={["Suzhou", "Innovation Center"]} />
              </h2>
              <FadeIn delay={0.15}>
                <p className="mt-6 text-base text-slate-400 leading-relaxed">
                  ACTIVE's Research & Development activities are based in Suzhou, one of China's most advanced technology ecosystems — recognized globally as one of Asia's leading innovation hubs, hosting international R&D centers, high-tech industries and advanced manufacturing companies.
                </p>
                <p className="mt-4 text-base text-slate-400 leading-relaxed">
                  This environment allows ACTIVE to collaborate with highly specialized engineering teams while accelerating product development and innovation.
                </p>
              </FadeIn>
            </div>
            <FadeIn delay={0.2}>
              <div className="rounded-2xl border border-white/10 bg-surface/60 p-8">
                <Overline light>Our R&D activities</Overline>
                <div className="mt-6 flex flex-wrap gap-2">
                  {RD_ACTIVITIES.map((a) => (
                    <span key={a} className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300 border border-white/15 rounded-full px-3.5 py-1.5 hover:border-signal/60 hover:text-white transition-colors duration-200">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 sm:py-32" data-testid="company-manufacturing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <Chapter index="03" label="Manufacturing Excellence" />
              <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-slate-950 leading-[1.05]">
                <MaskedLines lines={["Built on more than", "25 years of experience"]} />
              </h2>
              <FadeIn delay={0.15}>
                <p className="mt-6 text-base text-slate-600 leading-relaxed">
                  ACTIVE products are manufactured in Pujiang County, China, within a production facility specialized in stairlift manufacturing for more than 25 years — with over 25,000 stairlifts produced and installed worldwide.
                </p>
                <p className="mt-4 text-base text-slate-600 leading-relaxed">
                  The manufacturing organization combines experienced production teams, advanced quality control procedures and continuous process improvement to ensure premium reliability and consistent quality.
                </p>
                <div className="mt-9 grid grid-cols-3 gap-6">
                  <div>
                    <p className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-950">25<span className="text-signal text-xl align-top">+</span></p>
                    <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">Years of production</p>
                  </div>
                  <div>
                    <p className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-950">25k<span className="text-signal text-xl align-top">+</span></p>
                    <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">Stairlifts installed</p>
                  </div>
                  <div>
                    <p className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-950">100<span className="text-signal text-xl align-top">%</span></p>
                    <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">End-of-line tested</p>
                  </div>
                </div>
              </FadeIn>
            </div>
            <FadeIn delay={0.2}>
              <div className="spotlight-frame rounded-2xl overflow-hidden border border-slate-200/80">
                <img src={IMAGES.factory} alt="ACTIVE manufacturing facility" loading="lazy" className="w-full h-[380px] object-cover" />
              </div>
            </FadeIn>
          </div>

          <div className="mt-24">
            <Chapter index="04" label="Our Foundation" />
            <div className="grid grid-cols-2 md:grid-cols-5 gap-y-10" data-testid="company-timeline">
              {TIMELINE.map((t, i) => (
                <FadeIn key={t.year} delay={i * 0.08}>
                  <div className="relative pr-6">
                    <div className="flex items-center gap-3">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${i === TIMELINE.length - 1 ? "bg-signal" : "bg-slate-950"}`} />
                      <span className="hidden md:block h-px flex-1 bg-slate-200" />
                    </div>
                    <p className={`mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tighter ${i === TIMELINE.length - 1 ? "text-signal" : "text-slate-950"}`}>
                      {t.year}
                    </p>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500 leading-relaxed">{t.title}</p>
                    <p className="mt-2 text-[13px] text-slate-600 leading-relaxed hidden sm:block">{t.text}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] border-y border-slate-200/70 py-24" data-testid="company-rail">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <FadeIn>
            <div className="spotlight-frame rounded-2xl overflow-hidden border border-slate-200/80">
              <img src={IMAGES.machining} alt="Precision rail engineering" loading="lazy" className="w-full h-[340px] object-cover" />
            </div>
          </FadeIn>
          <div>
            <Chapter index="05" label="European Rail Manufacturing" />
            <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-slate-950 leading-[1.05]">
              <MaskedLines lines={["Global efficiency,", "European rail excellence"]} />
            </h2>
            <FadeIn delay={0.15}>
              <p className="mt-6 text-base text-slate-600 leading-relaxed">
                While our drive systems and mechanical platforms benefit from decades of manufacturing expertise, ACTIVE's strategic vision includes the development and production of advanced rail technologies in Europe — supporting faster delivery, greater flexibility and continuous innovation.
              </p>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                Future developments include the proprietary <span className="text-slate-950 font-medium">MODULAR RAIL™</span> platform, designed to simplify installation and redefine stairlift manufacturing.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-obsidian noise-dark py-24 sm:py-32" data-testid="company-platform">
        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="06" label="A Global Manufacturing Platform" light />
          <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-[1.05] max-w-3xl">
            <MaskedLines lines={["More than a", "stairlift manufacturer"]} />
          </h2>
          <FadeIn delay={0.15}>
            <p className="mt-6 text-base text-slate-400 leading-relaxed max-w-2xl">
              We are building an integrated industrial platform that enables premium products, flexible customization and industrial scalability — while maintaining the highest standards of quality and reliability.
            </p>
          </FadeIn>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {PLATFORM_PILLARS.map((p, i) => (
              <FadeIn key={p} delay={(i % 4) * 0.06}>
                <div className="h-full rounded-2xl border border-white/10 bg-surface/60 p-6 hover:border-signal/50 transition-colors duration-300" data-testid={`platform-pillar-${i}`}>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 font-display text-base sm:text-lg font-semibold tracking-tight text-white leading-snug">{p}</h3>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 sm:py-32" data-testid="company-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <Chapter index="07" label="Looking Ahead" />
          <FadeIn>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
              Innovation is at the core of everything we do. From <span className="text-slate-950 font-medium">CDRIVE™</span> Brushless Technology to <span className="text-slate-950 font-medium">WSAFE™</span> Outdoor Technology, and from future <span className="text-slate-950 font-medium">MODULAR RAIL™</span> systems to AI-powered stair measurement, ACTIVE is continuously investing in technologies that simplify installation, improve user experience and create new opportunities for distributors.
            </p>
          </FadeIn>
          <h2 className="mt-12 font-display text-2xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-950 leading-[1.08]">
            <MaskedLines lines={["To become the world's most innovative", "stairlift manufacturing platform."]} />
          </h2>
        </div>
      </section>

      <CTABand
        overline="Company"
        lines={["European vision.", "Global engineering.", "One platform."]}
      />
    </>
  );
}
